import { Injectable, signal } from '@angular/core';
import {
  job_opening, job_application, app_status, job_emp_type, job_status, job_public_url
} from './recruitment.models';

@Injectable({ providedIn: 'root' })
export class RecruitmentService {
  jobs = signal<job_opening[]>([]);
  apps = signal<job_application[]>([]);

  private next_job_id = this.jobs()?.length + 1;
  private next_app_id = this.apps()?.length + 1;

  get_job(job_code: string): job_opening | undefined {
    const code = decodeURIComponent(job_code || '').toUpperCase();
    return this.jobs().find(j => j.job_code.toUpperCase() === code);
  }

  open_jobs(): job_opening[] {
    return this.jobs().filter(j => j.status === 'open')
      .sort((a, b) => b.posted_date.localeCompare(a.posted_date));
  }

  add_job(input: {
    title: string; department: string; emp_type: job_emp_type;
    positions: number; description: string; requirements: string;
    closing_date: string; status: job_status;
  }): job_opening {
    const n = this.jobs()?.length + 1;
    const job: job_opening = {
      id: n,
      job_code: `JOB-2026-${String(n).padStart(3, '0')}`,
      title: input.title, department: input.department,
      emp_type: input.emp_type, positions: input.positions, filled: 0,
      description: input.description, requirements: input.requirements,
      posted_date: new Date().toISOString().substring(0, 10),
      closing_date: input.closing_date, status: input.status,
    };
    this.jobs.update(l => [...l, job]);
    return job;
  }

  update_job(id: number, patch: Partial<Omit<job_opening, 'id' | 'job_code'>>): job_opening | undefined {
    let updated: job_opening | undefined;
    this.jobs.update(list => list.map(j => {
      if (j.id !== id) return j;
      const positions = patch.positions ?? j.positions;
      const filled = Math.min(patch.filled ?? j.filled, positions);
      updated = { ...j, ...patch, id: j.id, job_code: j.job_code, positions, filled };
      return updated;
    }));
    return updated;
  }

  set_job_status(id: number, status: job_status): job_opening | undefined {
    return this.update_job(id, { status });
  }

  delete_job(id: number): void {
    const job = this.jobs().find(j => j.id === id);
    this.jobs.update(l => l.filter(j => j.id !== id));
    if (job) this.apps.update(l => l.filter(a => a.job_code !== job.job_code));
  }
  
  add_application(input: {
    job_code: string; job_title: string; applicant_name: string;
    email: string; phone: string; experience: number; remarks: string;
  }): job_application {
    const rec: job_application = {
      id: this.next_app_id++,
      job_code: input.job_code,
      job_title: input.job_title,
      applicant_name: input.applicant_name,
      email: input.email,
      phone: input.phone,
      experience: input.experience,
      applied_date: new Date().toISOString().substring(0, 10),
      status: 'applied',
      remarks: input.remarks,
    };
    this.apps.update(l => [rec, ...l]);
    return rec;
  }

  set_app_status(id: number, status: app_status): void {
    this.apps.update(l => l.map(a => a.id === id ? { ...a, status } : a));
  }

  share_url(job_code: string): string {
    return job_public_url(job_code);
  }
}
